import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { Clickable } from '@carvana/merch-components';
import cx from 'classnames';
import { useContext, useEffect } from 'react';
import useGetShowEvBanner from '@/MainLayout/useGetShowEvBanner';
import { useIsMobile } from '~/contexts/DeviceContext';
import { useConfiguration } from '~/contexts/StaticContext';
import usePriceDrop from '~/hooks/usePriceDrop';
import Close from '~/icons/Close';
import { PromoBanners } from './constants';
import {
  useHandleBannerClicks,
  useGetBannerAssets,
  useHandleShowLogic
} from './hooks';
import {
  BackGroundImage,
  BannerWrapper,
  CloseWrapper,
  ContentWrapper,
  PlugIcon,
  PriceDropArrow,
  TextWrapper
} from './PromoBanner.styles';
import { getBannerName } from './utils';

export default function PromoBanner() {
  const { showEarthDayBanner } = useConfiguration();
  const showEvBanner = useGetShowEvBanner();
  const defaultBanner: PromoBanners = showEarthDayBanner
    ? PromoBanners.Earth
    : PromoBanners.PriceDrop;
  const banner = showEvBanner ? PromoBanners.Ev : defaultBanner;
  const isMobile = useIsMobile();
  const { restrictedState } = usePriceDrop();
  const { backgroundImg, vehicleImg, iconBgColor, linkText, titleText } =
    useGetBannerAssets(banner);
  const { trackEvent } = useContext(AnalyticsContext);
  const { shouldShow, setLocalDismissed } = useHandleShowLogic(banner);

  const { handleCloseClick, handleShopAllClick } = useHandleBannerClicks(
    banner,
    setLocalDismissed
  );
  const bannerName = getBannerName(banner, restrictedState);

  useEffect(() => {
    if (!shouldShow) return;
    trackEvent('Viewed - Banner', { category: bannerName });
  }, [bannerName, shouldShow, trackEvent]);

  return shouldShow ? (
    <BannerWrapper data-qa="promo-banner">
      <BackGroundImage
        alt="swoosh image"
        src={`${process.env.CVNA_APP_ASSETS_CDN}${backgroundImg}`}
      />
      <div
        className={cx(
          'absolute full h-[75px] rounded-full w-[75px] m:-translate-y-1/2 m:h-[224px] m:left-[-73px] m:top-1/2 m:w-[224px]',
          { 'right-[-22px] bottom-[-22px]': isMobile },
          iconBgColor
        )}
        id="icon-wrapper"
      >
        {banner === PromoBanners.PriceDrop ? (
          <PriceDropArrow isRestricted={restrictedState} />
        ) : (
          <PlugIcon removeCenter={banner === PromoBanners.Earth} />
        )}
      </div>
      <ContentWrapper>
        <TextWrapper>
          {banner === PromoBanners.Ev && (
            <p className="t-label-s m:t-label-m">
              We’ve got an EV for Everyone
            </p>
          )}
          <p className="t-header-m m:t-header-xl">{titleText}</p>
          <Clickable
            data-qa="promo-banner-shop-all"
            data-analytics-id="Banner"
            data-analytics-attributes={JSON.stringify({
              category: bannerName,
              label: 'Shop All'
            })}
            className="t-link-s m:t-link-m"
            onClick={handleShopAllClick}
            key={banner}
          >
            {linkText}
          </Clickable>
        </TextWrapper>
        <img
          className="flex-shrink hidden max-w-[299px] w-full xl:block"
          src={`${process.env.CVNA_APP_ASSETS_CDN}${vehicleImg}`}
          alt="Tesla with Carvana halo"
        />
      </ContentWrapper>

      <Clickable
        data-qa="promo-banner-close"
        data-analytics-id="Banner"
        data-analytics-attributes={JSON.stringify({
          category: bannerName,
          label: 'Close'
        })}
        onClick={handleCloseClick}
        element={CloseWrapper}
      >
        <Close className="fill-blue-6" width={'24px'} height={'24px'} />
      </Clickable>
    </BannerWrapper>
  ) : null;
}
